import React, { useCallback, useEffect } from 'react'
import { FlatList, View, Platform } from 'react-native'
import {
  ATLButton,
  ATLCheckbox,
  ATLDialog,
  ATLDivider,
  ATLSpacing,
  ATLTextBody,
  DialogTypeEnum,
  FontWeightEnum,
  SpacingSizesEnum,
  TextAlignmentEnum,
} from '@appsimples/atlantica-react-native'
import { atlTheme } from '../../atlTheme'
import { DividerTypesEnum } from '@appsimples/atlantica-react-native/build/src/molecules/ATLDivider'
import styled from 'styled-components/native'
import { formatMessage } from '../../constants/strings/intl/intlHooks'
import { messages } from '../../constants/strings/messages'
import { Coordinator } from '../../navigation/coordinator/coordinator'
import { usePromise } from '../../utils/hooks/usePromise'
import { getStatement } from '../../services/main/getStatement'
import { useGlobal } from '../../global/useGlobal'
import { Loading } from '../../components/loading/Loading'
import { ifMoneyInt } from '../../utils/formatters'
import { EmptyCard } from './components/EmptyCard'
import { getUserSaleContracts } from '../../services/main/getUserSaleContracts'
import { getInstallmentDateAndTypeLabel } from './constants'
import { GetStatement_listStatements_installmentsComing } from '../../services/types/GetStatement'
import { onPressSAC } from './utils/onPressSAC'
import { MAX_BANK_SLIP } from '../../constants/maxBankSlip'
import { useLegacyState } from '../../utils/hooks/useLegacyState'
import { useGetManySecondBankSlip } from './utils/useGetManySecondBankSlips'
import { Modal } from './components/Modal'
import { timeout } from '../../utils/timeout'

interface Installment extends GetStatement_listStatements_installmentsComing { }

const isWeb = Platform.OS === 'web'
let ButtonWeb
if (isWeb) {
  const Button = require('@material-ui/core/Button').default
  const styledWeb = require('styled-components').default
  ButtonWeb = styledWeb(Button)`
    background-color: ${atlTheme.ColorBrand1Default} !important;
    font-weight: 500;
    border-radius: ${atlTheme.BorderRadiusFloatingButton}px !important;
    font-family: ${atlTheme.FontFamilySystem} !important;
    font-weight: 700 !important;
    color: rgb(252, 252, 252) !important;
    height: 48px !important;
    width: 100%;
  `
}

const buildInstallmentUniqueId = (installment: Installment) => {
  return `${installment.receivableBillId}-${installment.installmentId}`
}

const ScreenContainer = styled.SafeAreaView`
  height: 100%;
  background-color: ${atlTheme.ColorSurface};
  position: relative;
`

const ButtonContainer = styled.View`
  position: absolute;
  width: 100%;
  background-color: ${atlTheme.ColorSurface};
  bottom: 0;
  ${isWeb ? 'padding: 16px; position:fixed;' : ``}
`

const ItemContainer = styled.View`
  margin-vertical: ${atlTheme.SpacingAtomic}px;
  display: flex;
  flex-direction: row;
`

const TextContainer = styled.View`
  margin-left: -40px;
`

export const FinanceFutureScreen: React.FC = () => {
  const [installmentsComing] = useGlobal('installmentsComing')
  const [userSaleContracts] = useGlobal('userSaleContracts')
  const [actualSaleContract] = useGlobal('actualSaleContract')
  const [secondBankSlipRegistered] = useGlobal('duplicatesBankSlip')

  const { loading: isGetInfoLoading, request: getInfo } = usePromise(getStatement)
  const { loading: isGetConstructionsLoading, request: getConstructions } = usePromise(getUserSaleContracts)
  const { getManyDuplicateBankSlip, loadingBankSlip } = useGetManySecondBankSlip(async installment => {
    getInfo({ saleContractId: actualSaleContract.id })
    Coordinator.goToPayInstallment(installment)
  })

  const [state, setState] = useLegacyState({
    installmentsSelected: [],
    installments: [],
    isAddInstallmentDisabled: false,
    isModalEnable: false,
    checkBoxValue: {},
  })
  const { installmentsSelected, installments, isAddInstallmentDisabled, isModalEnable, checkBoxValue } = state

  const quantityBankSlipRegister = installmentsSelected.length + secondBankSlipRegistered?.length
  const installmentsLengthComparison = quantityBankSlipRegister > MAX_BANK_SLIP
  const loading = isGetInfoLoading || isGetConstructionsLoading || loadingBankSlip

  useEffect(() => {
    if (installmentsComing) setState({ installments: installmentsComing?.slice(0, 5) })
  }, [installmentsComing])

  useEffect(() => {
    getConstructions()
  }, [])

  useEffect(() => {
    if (actualSaleContract?.id) {
      getInfo({ saleContractId: actualSaleContract.id })
    }
    installmentsComing?.length === 0 && setState({ isAddInstallmentDisabled: true })
  }, [actualSaleContract])

  const handleSelectionBankSlip = (installment: Installment) => {
    const uniqueId = buildInstallmentUniqueId(installment)
    const isSelected = checkBoxValue?.[uniqueId]
    setState({ checkBoxValue: { [uniqueId]: !isSelected } })
  }

  const mapInstallmentsComing = useCallback(() => {
    return installments?.map(installment => {
      const uniqueId = buildInstallmentUniqueId(installment)
      return {
        value: checkBoxValue?.[uniqueId],
        onChange: () => handleSelectionBankSlip(installment),
        date: new Date(installment?.dueDate),
        conditionDescription: installment.conditionDescription,
        originalValue: installment.originalValue,
        currentBalance: installment.currentBalance
      }
    })
  }, [installments, checkBoxValue])

  const cleanInstallmentsSelectedState = () => {
    setState({ installmentsSelected: [], checkBoxValue: {}, isModalEnable: false })
  }

  const getActualContract = (id: string) => {
    return userSaleContracts.find(contract => {
      return contract.id === id
    })
  }

  const handleRegisterBankSlip = () => {
    getManyDuplicateBankSlip(installmentsSelected)
    cleanInstallmentsSelectedState()
  }

  const renderItem = ({ item }) => {
    return (
      <ItemContainer>
        <ATLCheckbox value={item.value} onChange={item.onChange} />
        <TextContainer>
          <ATLTextBody
            verticalMargin={0.1}
            labelBaseProps={{ multilineLimit: 1, textColor: atlTheme.ColorMediumContrast }}
          >
            {getInstallmentDateAndTypeLabel(item.date, item.conditionDescription)}
          </ATLTextBody>
          <ATLTextBody verticalMargin={0.1} labelBaseProps={{ multilineLimit: 1 }}>
            {`${ifMoneyInt(item.currentBalance)}`}
          </ATLTextBody>
        </TextContainer>
      </ItemContainer>
    )
  }

  const onEndReached = () => {
    const installmentsLength = installments.length
    setState({ installments: installmentsComing?.slice(0, installmentsLength + 10) })
  }

  const onPressDismiss = () => {
    setState({ isModalEnable: false })
  }

  const onPressConfirm = () => {
    setState({ isModalEnable: false })
    onPressSAC(installmentsSelected)
  }

  const onPressAnticipateInstallment = () => {
    const installmentsSelected = installments.filter(installment => {
      const uniqueInstallmentId = buildInstallmentUniqueId(installment)
      return checkBoxValue[uniqueInstallmentId]
    })
    if (installmentsSelected?.length) {
      setState({ installmentsSelected, isModalEnable: true })
    }
  }

  return loading ? (
    <>
      <ATLSpacing type={SpacingSizesEnum.marginal} />
      {loadingBankSlip && (
        <View style={{ width: '100%' }}>
          <ATLTextBody textAlignment={TextAlignmentEnum.center} labelBaseProps={{ multilineLimit: 4 }}>
            {messages.financeFutureScreen_loadingNewFutureBillets}
          </ATLTextBody>
        </View>
      )}
      <Loading />
    </>
  ) : (
    <ScreenContainer>
      <Modal
        type={DialogTypeEnum.alert}
        open={isModalEnable}
        onClose={onPressDismiss}
        onPressDismiss={onPressDismiss}
        onCancelButton={onPressDismiss}
        onPressConfirm={installmentsLengthComparison ? onPressConfirm : handleRegisterBankSlip}
        onConfirmButton={installmentsLengthComparison ? onPressConfirm : handleRegisterBankSlip}
        title={formatMessage(messages.financeFutureScreen_dialog_title)}
        description={
          installmentsLengthComparison
            ? formatMessage(messages.financeFutureScreen_dialog_unable)
            : formatMessage(messages.financeFutureScreen_dialog_message)
        }
        messageConfirmButton={messages.financeScreen_dialogButton_request}
        messageCancelButton={messages.financeScreen_dialogButton_cancel}
        buttonTexts={[
          formatMessage(messages.financeScreen_dialogButton_cancel),
          formatMessage(messages.financeScreen_dialogButton_request),
        ]}
        buttonBaseProps={{ textColorComplementary: atlTheme.ColorBrand2High, fontWeight: FontWeightEnum.medium }}
        titleLabelBaseProps={{ fontWeight: FontWeightEnum.medium }}
      />
      <View style={{ backgroundColor: atlTheme.ColorBackground, height: atlTheme.SpacingAtomic }} />
      <FlatList
        data={mapInstallmentsComing()}
        renderItem={renderItem}
        keyExtractor={(item, index) => index.toString()}
        ItemSeparatorComponent={() => {
          return <ATLDivider dividerBaseProps={{ color: atlTheme.ColorBackground }} />
        }}
        ListFooterComponent={<ATLSpacing type={SpacingSizesEnum.wide} />}
        ListEmptyComponent={() => {
          return <EmptyCard type={formatMessage(messages.financeScreen_installmentType_future)} />
        }}
        onEndReached={onEndReached}
        onEndReachedThreshold={0.1}
      />
      <ButtonContainer>
        <ATLDivider type={DividerTypesEnum.noSpacing} dividerBaseProps={{ color: atlTheme.ColorBackground }} />
        {isWeb ? (
          <ButtonWeb onClick={onPressAnticipateInstallment} disabled={isAddInstallmentDisabled}>
            {formatMessage(messages.financeFutureScreen_button_anticipateInstallment)}
          </ButtonWeb>
        ) : (
          <ATLButton
            title={formatMessage(messages.financeFutureScreen_button_anticipateInstallment)}
            onPress={onPressAnticipateInstallment}
            disabled={isAddInstallmentDisabled}
            horizontalMargin={atlTheme.SpacingDistribute}
            buttonBaseProps={{
              borderRadius: atlTheme.BorderRadiusFloatingButton,
              fontFamily: atlTheme.FontFamilySystem,
            }}
          />
        )}
      </ButtonContainer>
    </ScreenContainer>
  )
}
